import React, { useState } from 'react';
import { BUCKET_LABELS, BUCKET_DESCRIPTIONS } from './bucketLabels';

const BUCKET_PROMPT_TEMPLATES = {
  NEW_LISTINGS: "Focus on tokens recently listed on the exchange with a TVL > $100,000 USD. Prioritize those showing organic volume growth in their first 72 hours rather than single-spike activity. Prefer tokens with positive price momentum and sustainable funding rates. Cap assessment at 0.75 unless multi-signal strength is evident.",
  MOMENTUM: "Focus on tokens with the strongest directional price momentum across 24h, 7d, and 30d windows. Use rate of change as the primary ranking signal. Include tokens in strong downtrends if short opportunities are emphasized. Penalize tokens where momentum is decelerating (24h ROC much weaker than 7d ROC). Require volume support for high momentum scores.",
  TRENDING_SOCIAL: "Focus on tokens gaining outsized social attention. Primary signals: mention count growth rate, engagement score, and sentiment direction. A token trending with bullish sentiment scores higher than one trending on controversy. Use notable headlines to assess whether the signal is substantive (partnerships, upgrades) or noise.",
  FUNDING_RATE_ANOMALY: "Focus on tokens with funding rates deviating significantly from their recent average. Extreme positive funding signals overcrowded long positioning - a potential mean-reversion short opportunity. Extreme negative funding indicates overcrowded shorts. Require at least 3 consecutive periods of extreme rates before scoring above 0.7.",
  VOLUME_BREAKOUT: "Focus on tokens where current volume is spiking relative to the rolling average. A volume ratio above 3.0 is strong; above 5.0 is exceptional. Require the accompanying price change to be directional, not flat with high volume which may indicate distribution or wash trading.",
  WHALE_ACTIVITY: "Focus on tokens showing significant large-position changes. Primary signals: net whale flow direction, whale count, and accumulation/distribution bias. Accumulation bias with high whale count is the strongest signal. Single-whale moves should score lower than distributed accumulation across multiple wallets.",
  SECTOR_ROTATION: "Focus on identifying which sectors (L1, DeFi, AI, meme, gaming) are showing aggregate momentum or volume rotation. Propose tokens from the top 2 sectors by aggregate momentum. Within each hot sector, rank by individual token momentum to select the strongest representatives.",
  CORRELATION_DIVERGENCE: "Focus on tokens breaking their historical correlation with BTC and ETH. A token that was highly correlated but is now diverging may be developing an independent catalyst. Prioritize tokens where both BTC and ETH correlation have dropped simultaneously over tokens where only one dropped.",
  LIQUIDATION_CLUSTERS: "Focus on tokens with significant liquidation density at nearby price levels. High OI concentration near current price means a small move could trigger cascading liquidations - creating both opportunity and risk. Score tokens where the liquidation cluster is directionally aligned with recent price momentum higher.",
};

export default function ResearchConfigForm({
  agentId,        // when set, CRUD uses /agents/{agentId}/research/config
  portfolioId,    // fallback for legacy portfolio-scoped URLs
  apiBaseUrl: apiBaseUrlProp,
  authToken,
  config,
  onSaved,
  onCancel,
}) {
  const apiBaseUrl = apiBaseUrlProp || window.location.origin.replace('/web', '');

  const [bucket, setBucket] = useState(config?.bucket || '');
  const [methodologyPrompt, setMethodologyPrompt] = useState(config?.methodology_prompt || '');
  const [isActive, setIsActive] = useState(config?.is_active ?? true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPromptSuggestion, setShowPromptSuggestion] = useState(false);

  const isEditMode = !!config;
  const promptLength = methodologyPrompt.length;
  const PROMPT_MAX_LENGTH = 5000;
  const PROMPT_MIN_LENGTH = 10;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Client-side validation
    if (!bucket) {
      setError('Please select a bucket');
      return;
    }

    if (methodologyPrompt.length < PROMPT_MIN_LENGTH) {
      setError(`Methodology prompt must be at least ${PROMPT_MIN_LENGTH} characters (currently ${promptLength})`);
      return;
    }

    if (methodologyPrompt.length > PROMPT_MAX_LENGTH) {
      setError(`Methodology prompt must be at most ${PROMPT_MAX_LENGTH} characters (currently ${promptLength})`);
      return;
    }

    setIsSubmitting(true);

    try {
      const url = agentId
        ? (isEditMode
            ? `${apiBaseUrl}/agents/${agentId}/research/config/${config.id}`
            : `${apiBaseUrl}/agents/${agentId}/research/config`)
        : (isEditMode
            ? `${apiBaseUrl}/portfolio/${portfolioId}/research/config/${config.id}`
            : `${apiBaseUrl}/portfolio/${portfolioId}/research/config`);

      const body = {
        bucket,
        methodology_prompt: methodologyPrompt,
        is_active: isActive,
      };

      const headers = { 'Content-Type': 'application/json' };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const savedConfig = await response.json();
      onSaved(savedConfig);
    } catch (err) {
      console.error('Failed to save research config:', err);
      setError(err.message || 'Failed to save research config');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (confirm('Are you sure you want to cancel? Unsaved changes will be lost.')) {
      onCancel();
    }
  };

  const handleApplyPromptTemplate = () => {
    if (bucket && BUCKET_PROMPT_TEMPLATES[bucket]) {
      setMethodologyPrompt(BUCKET_PROMPT_TEMPLATES[bucket]);
      setShowPromptSuggestion(false);
    }
  };

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
      <h3 className="text-lg font-semibold text-white mb-4">
        {isEditMode ? 'Edit Research Config' : 'Create Research Config'}
      </h3>

      {error && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-800 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-red-300 text-sm">{error}</div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Bucket Select */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Research Bucket <span className="text-red-400">*</span>
          </label>
          <select
            value={bucket}
            onChange={(e) => { setBucket(e.target.value); setMethodologyPrompt(''); }}
            disabled={isSubmitting}
            className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2.5 text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-colors"
            required
          >
            <option value="">Select a bucket...</option>
            {Object.entries(BUCKET_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Methodology Prompt */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Methodology Prompt <span className="text-red-400">*</span>
          </label>
          <textarea
            value={methodologyPrompt}
            onChange={(e) => setMethodologyPrompt(e.target.value)}
            disabled={isSubmitting}
            placeholder="Describe your research methodology. For example: Focus on tokens with strong upward momentum in the AI and DeFi sectors. Prefer tokens where the 24h ROC is accelerating relative to the 7d trend. Use the suggestion link below if you need help getting started with a prompt for this bucket type."
            className={`w-full bg-slate-700/50 border rounded-lg px-3 py-2.5 text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-colors font-mono text-sm resize-y`}
            rows={6}
            maxLength={PROMPT_MAX_LENGTH}
            required
          />
          <div className="flex items-center justify-between mt-2">
            <div className={`text-sm ${promptLength > PROMPT_MAX_LENGTH ? 'text-red-400' : promptLength < PROMPT_MIN_LENGTH ? 'text-amber-400' : 'text-slate-400'}`}>
              {promptLength} / {PROMPT_MAX_LENGTH} characters
              {promptLength > 0 && promptLength < PROMPT_MIN_LENGTH && (
                <span className="ml-2 text-xs">(minimum {PROMPT_MIN_LENGTH} characters required)</span>
              )}
            </div>
            {bucket && (
              <button
                type="button"
                onClick={() => setShowPromptSuggestion(!showPromptSuggestion)}
                className="text-cyan-400 hover:text-cyan-300 text-sm font-medium flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                Suggest prompt for {BUCKET_LABELS[bucket]}
              </button>
            )}
          </div>

          {/* Prompt Suggestion Dropdown */}
          {showPromptSuggestion && bucket && (
            <div className="mt-3 p-4 bg-cyan-900/20 border border-cyan-700/50 rounded-lg">
              <p className="text-sm text-cyan-200 mb-3 font-medium">
                Suggested prompt for {BUCKET_LABELS[bucket]}:
              </p>
              <p className="text-sm text-cyan-100/80 mb-4 leading-relaxed">
                {BUCKET_PROMPT_TEMPLATES[bucket]}
              </p>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowPromptSuggestion(false)}
                  className="px-3 py-1.5 text-sm text-slate-300 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors border border-slate-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleApplyPromptTemplate}
                  className="px-3 py-1.5 text-sm text-white bg-cyan-600 hover:bg-cyan-700 rounded-lg transition-colors"
                >
                  Use This Prompt
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Set as Active */}
        {!isEditMode && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={isSubmitting}
              className="w-4 h-4 text-cyan-600 bg-slate-700 border-slate-600 rounded focus:ring-cyan-500"
            />
            <label htmlFor="isActive" className="text-sm text-slate-300">
              Set as active config (will deactivate any other active config for this portfolio)
            </label>
          </div>
        )}

        {/* Buttons */}
        <div className="flex items-center gap-3 pt-4 border-t border-slate-700/50">
          <button
            type="submit"
            disabled={isSubmitting || !bucket || promptLength < PROMPT_MIN_LENGTH}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
              isSubmitting || !bucket || promptLength < PROMPT_MIN_LENGTH
                ? 'bg-slate-600/50 text-slate-400 cursor-not-allowed'
                : 'bg-cyan-600 hover:bg-cyan-700 text-white'
            }`}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {isEditMode ? 'Saving...' : 'Creating...'}
              </>
            ) : (
              isEditMode ? 'Save Changes' : 'Create Config'
            )}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSubmitting}
            className="px-4 py-2.5 rounded-lg font-medium text-slate-300 bg-slate-700/50 hover:bg-slate-700 transition-colors border border-slate-600"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
